(ns ui.view.entity-creator.investor.view
  (:require
   [clojure.string :as string]
   [reagent.core :as r]
   [re-frame.core :as rf]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.entity-type-names :as etn]
   [common.schema.registry :as registry]
   [frontend.state.current-session :as session]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as input]
   [ui.view.entity-creator.components :as creator-components]
   [ui.view.entity-creator.view :as creator]
   [ui.view.entity-creator.model :as creator-m]
   [ui.view.entity-creator.utils :as creator-u]
   [ui.view.entity-creator.editor :as editor]
   [ui.view.entity-creator.investor.event]
   [ui.view.entity-creator.investor.sub]
   [ui.view.entity-creator.investor.model :as m]
   [ui.util :as uu]))

(defn title-view
  [_args]
  [:div (loc/tr :create-new-investor-profile)])

(defn legal-entity-type-lc
  [entity-type]
  (string/lower-case
   (etn/get-name entity-type :en)))

(defn legal-entity-selector
  [{:keys [legal-entity-type] :as args}]
  (r/with-let [;; the id of the legal entity that is loading from the backend, or nil
               loading* (r/atom nil)]
    (let [legal-entity @(rf/subscribe [m/selected-legal-entity args])
          legal-entity-id (:db/id legal-entity)
          legal-entity-option (when legal-entity
                                {:value legal-entity-id
                                 :label (entity/display-string legal-entity)})
          le-lc-label (legal-entity-type-lc legal-entity-type)
          label (str "Select "
                     le-lc-label
                     " as legal entity")]
      [:div
       [input/view
        {:data-type :editor.type/ref-single
         :label label
         :mandatory? true
         :editor-type :editor.type/search
         :value legal-entity-option
         :create! (fn [query]
                    (rf/dispatch
                     [m/confirm-legal-entity
                      (-> args
                          (assoc :legal-entity-type legal-entity-type
                                 :initial-value query))]))
         :field/entity-types [legal-entity-type]
         :placeholder (str "Select " le-lc-label)
         :on-change (fn [val _opt-val]
                      (if (nil? val)
                        (rf/dispatch [m/unselect-legal-entity
                                      (assoc args :legal-entity-id val)])
                        ;; Load the legal entity from the backend, always, also when
                        ;; the session already holds it. A second selection of the same
                        ;; entity does not fetch again. The fetch also brings the
                        ;; investor of the legal entity into the session, where the
                        ;; check for an existing investor still reads it. If the load
                        ;; fails, say so and stay on the picker.
                        (creator-u/load-selected!
                         {:loading* loading*
                          :entity-id val
                          :on-loaded (fn [entity _entities]
                                       (rf/dispatch [m/confirm-legal-entity
                                                     (assoc args
                                                            :legal-entity-id val
                                                            :legal-entity-data entity)]))
                          :on-error  (fn [_error]
                                       (rf/dispatch [m/legal-entity-load-failed]))})))}]
       (when @loading*
         [:div
          {:class (uu/cm "text-sm pt-1")}
          (loc/tr :loading)])])))

(defn get-legal-entity-creator-args
  [{:keys [legal-entity-type] :as args}]
  (let [entity-type legal-entity-type
        config-path (:main-config-path
                     (ecu/resolve-config-paths
                      {:entity-type entity-type}))
        creator-ident (m/resolve-legal-entity-creator-ident args)
        creator-args {:ident creator-ident
                      :entity-type entity-type
                      :config-path config-path
                      :skip-success-msg? true
                      :entity-edits-path creator-m/entity-edits-path}]
    creator-args))

(defn legal-entity-creator
  [{:keys [legal-entity-type] :as args}]
  (r/with-let [entity-type legal-entity-type
               legal-entity @(rf/subscribe [m/selected-legal-entity args])
               person? (= entity-type :entity.type/person)
               legal-entity-name (entity/display-string legal-entity)
               creator-args (get-legal-entity-creator-args args)
               creator-ident (:ident creator-args)
               name-attribute (registry/resolve-name-attribute entity-type)

               initial-values (if person?
                                (select-keys legal-entity
                                             [:person/first-name :person/last-name
                                              :person/universal-email-address])
                                {name-attribute legal-entity-name})
               init-args (assoc creator-args
                                :initial-values
                                initial-values)
               ;; TODO potential future update: better handling of initialization
               ;;      between 'back'/'next' use case
               previous-legal-entity nil;;@(rf/subscribe [m/creator-previous-target args])
               previous-legal-entity-type (:entity/type previous-legal-entity)
               previous-legal-entity-name (entity/display-string previous-legal-entity)
               initialize? (or (not= previous-legal-entity-type legal-entity-type)
                               (not previous-legal-entity-type))
               re-prefill-name? (and (not initialize?)
                                     (not person?)
                                     (not= legal-entity-name previous-legal-entity-name))
               ;;_ (rf/dispatch [m/set-creator-previous-target args])
               _ (when initialize?
                   (rf/dispatch-sync [creator-m/initialize init-args]))
               _ (when re-prefill-name?
                   ;;TODO for person: m/edit-values select-keys [first,last,email]
                   (rf/dispatch-sync [creator-m/edit-value
                                      (assoc creator-args
                                             :attribute name-attribute
                                             :new-value legal-entity-name)]))]
    (let [config-path (:main-config-path
                       (ecu/resolve-config-paths
                        {:entity-type entity-type}))
          creator-args (assoc args
                              :ident creator-ident
                              :entity-type entity-type
                              :config-path config-path
                              :entity-edits-path creator-m/entity-edits-path)
          company? (= entity-type :entity.type/company)]
      [:div
       [creator/creator-view
        (cond-> creator-args
          true (assoc :auto-focus-first-blank? true)
          company? (assoc :field-schema-overrides
                          {:asset/name
                           {:field/translations
                            {:en (loc/tr :company-name)}}}))]])))

(defn legal-entity-view
  [args]
  (let [legal-entity @(rf/subscribe [m/selected-legal-entity args])
        legal-entity-id (:db/id legal-entity)
        legal-entity-type (:entity/type legal-entity)
        existing? (number? legal-entity-id)]
    (if existing?
      [:div
       [input/view
        {:editor-type :editor.type/text
         :data-type :data.type/text-simple
         :disabled? true
         :label (loc/tr :entity-type-and-name
                        [(etn/get-name legal-entity-type :en)])
         :value (entity/display-string legal-entity :long)}]]
      [legal-entity-creator args])))

(def investor-field-overrides
  {:investor/name {:read-only? true
                   :hidden? true}
   :investor/legal-entity {:read-only? true
                           :hidden? true}})

(defn get-investor-creator-args
  [args]
  (let [entity-type :entity.type/investor
        config-path (:main-config-path
                     (ecu/resolve-config-paths
                      {:entity-type entity-type}))
        creator-ident (m/resolve-investor-creator-ident args)
        creator-args {:ident creator-ident
                      :entity-type entity-type
                      :config-path config-path
                      :field-schema-overrides investor-field-overrides
                      :skip-success-msg? true
                      :entity-edits-path creator-m/entity-edits-path}]
    creator-args))

(defn investor-editor
  [args]
  (r/with-let [investor @(rf/subscribe [m/investor args])
               creator-args (get-investor-creator-args args)
               creator-ident (:ident creator-args)
               init-args (assoc creator-args :entity investor)
               previous-investor nil;;@(rf/subscribe [m/previous-investor args])
               initialize? (or (not= previous-investor investor)
                               (not previous-investor))
               ;;_ (rf/dispatch [m/set-creator-previous-investor args])
               _ (when initialize?
                   (rf/dispatch-sync [creator-m/initialize init-args]))]
    (let [entity-type :entity.type/investor
          investor @(rf/subscribe [m/investor args])
          full-config (:tenant/entity-creation-config @session/tenant)
          config-path (:main-config-path
                       (ecu/resolve-config-paths
                        {:entity-type entity-type}))
          field-schemas (registry/get-schema @session/tenant-schema
                                             entity-type)
          field-schema-overrides investor-field-overrides
          editor-args (assoc args
                             :ident creator-ident
                             :entity-type entity-type
                             :config-path config-path
                             :entity-edits-path creator-m/entity-edits-path)]
      [editor/view
       editor-args
       {:raw-config full-config
        :field-schemas field-schemas
        :field-schema-overrides field-schema-overrides
        :entity investor}])))

(defn investor-creator
  [args]
  (r/with-let [entity-type :entity.type/investor
               legal-entity @(rf/subscribe [m/selected-legal-entity args])
               legal-entity-name (entity/display-string legal-entity :long)
               legal-entity-id (:db/id legal-entity)
               ;;opportunity-name (unique-entity-name entity-type target-name)
               creator-args (get-investor-creator-args args)
               creator-ident (:ident creator-args)
               name-attribute (registry/resolve-name-attribute entity-type)
               init-args (assoc creator-args
                                :initial-values
                                {name-attribute legal-entity-name
                                 :investor/legal-entity legal-entity-id})
;;               previous-investor @(rf/subscribe
;;                                   [m/creator-previous-investor args])
               overrides investor-field-overrides
               previous-legal-entity nil;;@(rf/subscribe [m/creator-previous-legal-entity args])
               ;;previous-legal-entity-type (:entity/type previous-target)
               initialize? (or (not= previous-legal-entity legal-entity)
                               (nil? previous-legal-entity))
               re-prefill-name? (and (not initialize?)
                                     (not= legal-entity previous-legal-entity))
               ;;_ (rf/dispatch [m/set-creator-previous-investor args])
               _ (when initialize?
                   (rf/dispatch-sync [creator-m/initialize init-args]))
               _ (when re-prefill-name?
                   (rf/dispatch-sync [creator-m/edit-value
                                      (assoc creator-args
                                             :attribute name-attribute
                                             :new-value legal-entity-name)]))]
    (let [config-path (:main-config-path
                       (ecu/resolve-config-paths
                        {:entity-type entity-type}))
          creator-args (assoc args
                              :ident creator-ident
                              :entity-type entity-type
                              :field-schema-overrides overrides
                              :config-path config-path
                              :entity-edits-path creator-m/entity-edits-path)]
      [creator/creator-view
       creator-args])))

(defn investor-view
  [args]
  (let [investor @(rf/subscribe [m/investor args])
        legal-entity-type (:entity/type @(rf/subscribe
                                          [m/selected-legal-entity args]))
        creating? @(rf/subscribe [m/creating? args])
        investor-id (:db/id investor)
        existing? (number? investor-id)]
    [:div
     {:class (uu/cm "mt-4")}
     (when (and existing?
                ;; prevent flashing this note during creation phase:
                (not creating?))
       [:div
        {:class (uu/cm "flex flex-col items-start p-4 rounded shadow-md"
                       "bg-blue-50 text-blue-800"
                       "border-blue-400 border-l-4 mb-4")}
        [:div (loc/tr :info-note)]
        [:div (loc/tr :investor-creator/existing-investor-note
                      [(legal-entity-type-lc legal-entity-type)])]])
     [:div
      {:class (uu/cm "text-lg font-semibold _mt-4 mb-2 _ml-4")}
      (loc/tr :creator/investor-profile)]
     (if existing?
       [investor-editor args]
       [investor-creator args])]))

(defn entity-creator-view
  [args]
  [:div
   [legal-entity-view args]
   [investor-view args]])

(defn view
  [args]
  (let [subview @(rf/subscribe [m/subview args])]
    [:div
     {:class (uu/cm "mb-2 pb-2")}
     (case subview
       :select-legal-entity
       [legal-entity-selector args]

       :entity-creator
       [entity-creator-view args]

       [:div ""])]))

(defn legal-entity-footer
  [args]
  (let [selected-legal-entity @(rf/subscribe [m/selected-legal-entity args])]
    [:div
     [button/primary-solid
      {:on-click (fn [_]
                   (rf/dispatch [m/navigate-next args]))
       :disabled? (not selected-legal-entity)}
      (loc/tr :next)]]))

(defn validations-tooltip
  [{:keys [legal-entity-type] :as args}
   {:keys [investor-validations legal-entity-validations]}]
  (let [investor-tooltip
        (creator-components/entity-validations-tooltip
         (get-investor-creator-args args)
         (etn/get-name :entity.type/investor :en)
         investor-validations)
        legal-entity-tooltip
        (creator-components/entity-validations-tooltip
         (get-legal-entity-creator-args args)
         (etn/get-name legal-entity-type :en)
         legal-entity-validations)
        tooltip-items (->> [investor-tooltip
                            legal-entity-tooltip]
                           (filterv some?))]
    (cond
      (empty? tooltip-items)
      nil

      (= 1 (count tooltip-items))
      (first tooltip-items)

      :else
      (into
       [:div]
       (map-indexed (fn [idx item]
                      [:div
                       {:class (when (< idx
                                        (dec (count tooltip-items)))
                                 "mb-2")}
                       item]))
       tooltip-items))))

(defn entity-creator-footer
  [args]
  (let [investor @(rf/subscribe [m/investor args])
        existing? (number?
                   (:db/id investor))

        investor-creator-args (cond-> (get-investor-creator-args args)
                                existing?
                                (assoc :entity investor))
        validations @(rf/subscribe [creator-m/entity-validations
                                    investor-creator-args])
        legal-entity @(rf/subscribe [m/selected-legal-entity args])
        new-legal-entity? (string? (:db/id legal-entity))
        legal-entity-creator-args (get-legal-entity-creator-args args)
        ;; only for newly created person/company:
        legal-entity-validations (when new-legal-entity?
                                   @(rf/subscribe [creator-m/entity-validations
                                                   legal-entity-creator-args]))
        processing? @(rf/subscribe [m/processing? args])
        disabled? (or (seq validations)
                      (seq legal-entity-validations)
                      processing?)
        tooltip (validations-tooltip args
                                     {:investor-validations validations
                                      :legal-entity-validations legal-entity-validations})]
    [:div
     {:class (uu/cm "flex gap-4")}
     [button/secondary-hollow
      {:on-click (fn [_]
                   (rf/dispatch [m/navigate-back args]))}
      (loc/tr :back)]
     [button/primary-solid
      {:on-click (fn [_]
                   (rf/dispatch [m/start-create args]))
       :tooltip tooltip
       :disabled? disabled?}
      (loc/tr (cond
                processing? :processing
                existing? :update
                :else :create))]]))

(defn footer
  [args]
  (let [subview @(rf/subscribe [m/subview args])]
    (case subview
      :select-legal-entity
      [legal-entity-footer args]

      :entity-creator
      [entity-creator-footer args]

      nil)))

(defn add-ident
  [{:keys [ident] :as props}]
  (let [ident (if ident
                ident
                (-> "investor-creator-"
                    gensym
                    str
                    keyword
                    vector))]
    (assoc props :ident ident)))

(defn curtain
  [{:keys [close-modal! props] :as _modal-props}]
  (let [portal-target-atom (atom nil)
        ;; if props don't contain :ident => generate one:
        props (add-ident props)
        ;; legal-entity-type should be always passed. if not, the app will crash
        ;; => add default :entity.type/company just in case there are places
        ;;    where the investor creation flow is triggered without it
        ;;    TODO possibly a selector instead
        props (if (-> props :legal-entity-type keyword?)
                props
                (assoc props :legal-entity-type :entity.type/company))
        close! (fn [_]
                 (rf/dispatch [m/close-creator props])
                 (close-modal!))]
    ;;TODO remove 'when-not' - used only for development time convenience
    (when-not false #_(= [@(rf/subscribe [m/pipeline props])
                          @(rf/subscribe [m/stage props])]
                         [(:pipeline props) (:stage props)])

      ;;(prn (get-in @re-frame.db/app-db
      ;;             (:ident props)))
      (rf/dispatch-sync [m/initialize props {:on-ok! (:on-ok! props)
                                             :on-processed! (:on-processed! props)
                                             :close-modal! close-modal!}]))
    (fn [{:keys [shown? _topmost?]}]
      (let [finished? true;;@(rf/subscribe [m/created-entity props])
            props (assoc props :portal-target-atom portal-target-atom)]
        [modal/view
         ;; we want this visible even if not topmost, otherwise would cause
         ;; unwanted resetting of local-state
         {:visible?      shown?
          :hide!         (if finished?
                           (fn []
                             ;;(rf/dispatch [m/finish props])
                             (close! nil))
                           close!)
          :outer-content-class (uu/cm "justify-start")
          :header-child-class "truncate"
          :content-class (uu/cm
                          (if false
                            "w-9/12"
                            nil)
                          "mx-0 ml-auto h-full max-h-full mb-1"
                          "min-w-[320px] w-1/4")
          :close-button? true}
         [title-view props]
         [view props]
         [footer props]]))))
